import React, { useState, useCallback } from "react";
import { ChevronDown } from "react-feather";
import { EvidenceCard } from "../evidence-card/evidence-card";
import type { EvidenceCardConfig } from "../criterion-question/criterion-question";
import "./criterion-freetext.scss";

export type { EvidenceCardConfig };

export interface CriterionFreeTextProps {
  stepNumber?: number | string;
  title: string;
  badge?: React.ReactNode;

  textareaName?: string;
  textareaValue?: string;
  onTextChange?: (
    value: string,
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => void;
  textareaPlaceholder?: string;
  textareaDisabled?: boolean;
  textareaRows?: number;
  textareaMaxLength?: number;

  evidenceCards?: EvidenceCardConfig[];
  /** @deprecated Use evidenceCards instead */
  evidenceTitle?: string;
  /** @deprecated Use evidenceCards instead */
  evidenceStatements?: string[];
  /** @deprecated Use evidenceCards instead */
  evidenceCardBackGroundColor?: string;

  collapsible?: boolean;
  defaultExpanded?: boolean;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;

  /** Optional content rendered between the header row and the textarea. */
  headerSlot?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  id?: string;
}

const CriterionFreeText = ({
  stepNumber,
  title,
  badge,
  textareaName,
  textareaValue,
  onTextChange,
  textareaPlaceholder = "Enter your notes here…",
  textareaDisabled = false,
  textareaRows = 4,
  textareaMaxLength,
  evidenceCards,
  evidenceTitle,
  evidenceStatements,
  evidenceCardBackGroundColor,
  collapsible = false,
  defaultExpanded = true,
  expanded: expandedProp,
  onExpandedChange,
  headerSlot,
  children,
  className,
  id,
}: CriterionFreeTextProps) => {
  const isControlled = expandedProp !== undefined;
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const isExpanded = isControlled ? expandedProp : internalExpanded;

  const toggleExpanded = useCallback(() => {
    const next = !isExpanded;
    if (!isControlled) setInternalExpanded(next);
    onExpandedChange?.(next);
  }, [isExpanded, isControlled, onExpandedChange]);

  const titleId = id ? `${id}-title` : undefined;
  const textareaId = id ? `${id}-textarea` : undefined;

  const resolvedEvidenceCards: EvidenceCardConfig[] =
    evidenceCards != null && evidenceCards.length > 0
      ? evidenceCards
      : evidenceTitle != null ||
          (evidenceStatements != null && evidenceStatements.length > 0)
        ? [
            {
              title: evidenceTitle,
              statements: evidenceStatements,
              cardBackGroundColor: evidenceCardBackGroundColor,
            },
          ]
        : [];

  const hasEvidence = resolvedEvidenceCards.length > 0;

  return (
    <section
      id={id}
      className={["criterion-freetext", className].filter(Boolean).join(" ")}
      aria-labelledby={titleId}
    >
      {stepNumber != null && (
        <div className="criterion-freetext__step-column">
          <span className="criterion-freetext__step">{stepNumber}.</span>
        </div>
      )}

      <div className="criterion-freetext__body">
        <div className="criterion-freetext__header">
          <h3 id={titleId} className="criterion-freetext__title">
            {title}
          </h3>
          <div className="criterion-freetext__header-actions">
            {badge}
            {collapsible && (
              <button
                type="button"
                className={[
                  "criterion-freetext__toggle",
                  isExpanded ? "" : "criterion-freetext__toggle--collapsed",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={toggleExpanded}
                aria-expanded={isExpanded}
                aria-label={isExpanded ? "Collapse" : "Expand"}
              >
                <ChevronDown size={16} />
              </button>
            )}
          </div>
        </div>

        <div
          className={[
            "criterion-freetext__collapsible",
            isExpanded
              ? "criterion-freetext__collapsible--expanded"
              : "criterion-freetext__collapsible--collapsed",
          ].join(" ")}
          aria-hidden={!isExpanded}
        >
          <div className="criterion-freetext__collapsible-inner">
            {headerSlot != null && (
              <div className="criterion-freetext__header-slot">
                {headerSlot}
              </div>
            )}
            <div className="criterion-freetext__content">
              <textarea
                id={textareaId}
                name={textareaName}
                className="criterion-freetext__textarea"
                value={textareaValue}
                placeholder={textareaPlaceholder}
                disabled={textareaDisabled}
                rows={textareaRows}
                maxLength={textareaMaxLength}
                aria-labelledby={titleId}
                onChange={(e) => onTextChange?.(e.target.value, e)}
              />

              {hasEvidence &&
                resolvedEvidenceCards.map((card, index) => (
                  <EvidenceCard
                    key={index}
                    title={card.title}
                    statements={card.statements}
                    cardBackGroundColor={card.cardBackGroundColor}
                    titleAction={card.titleAction}
                  />
                ))}
            </div>

            {children != null && (
              <div className="criterion-freetext__children">{children}</div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export { CriterionFreeText };
export default CriterionFreeText;
